import React, { useState } from "react";
import toast from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';
import {
  analizarRecetaConIA,
  crearEntradaExpediente
} from "../../services/api";
import EntryMethodModal from "../EntryMethodModal";
import UploadPrescriptionModal from "../UploadPrescriptionModal";
import ReviewExtractedDataModal from "../ReviewExtractedDataModal";

export default function RegistrarVisita({
  show,
  objetoId,
  usuario,
  isVetAccess,
  onClose,
  onOpenManualEntry,
  onEntryCreated,
  onPostSaveTreatment
}) {
  const [showEntryMethodModal, setShowEntryMethodModal] = useState(show);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [prescriptionFile, setPrescriptionFile] = useState(null);

  function handleSelectAI() {
    setShowEntryMethodModal(false);
    setAnalysisError(null);
    setShowUploadModal(true);
  }

  function handleSelectManual() {
    handleCloseAll();
    onOpenManualEntry();
  }

  async function handleAnalyzeReceta(file) {
    setLoadingAnalysis(true);
    setAnalysisError(null);
    setPrescriptionFile(file);

    const result = await analizarRecetaConIA(objetoId, file);

    setLoadingAnalysis(false);

    if (result.success) {
      setExtractedData(result);
      setShowUploadModal(false);
      setShowReviewModal(true);
    } else {
      setAnalysisError(result);
    }
  }

  function handleRescan() {
    setShowReviewModal(false);
    setExtractedData(null);
    setAnalysisError(null);
    setShowUploadModal(true);
  }

  async function subirArchivoReceta(file) {
    let uidParaUpload = usuario?.ownerId;

    if (!uidParaUpload) {
      const nfcToken = localStorage.getItem('nfcToken');
      if (nfcToken) {
        try {
          const decoded = jwtDecode(nfcToken);
          uidParaUpload = decoded.uid || decoded.sub || decoded.userId || decoded.user_id;
        } catch (e) {
          console.warn('[subirArchivoReceta] No se pudo decodificar nfcToken');
        }
      }
    }

    if (!uidParaUpload) {
      uidParaUpload = `mascota_${objetoId}`;
      console.warn('[subirArchivoReceta] Usando fallback ownerId:', uidParaUpload);
    }

    console.log('[subirArchivoReceta] Subiendo archivo:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      ownerId: uidParaUpload,
      objetoId
    });

    const formDataUpload = new FormData();
    formDataUpload.append("ownerId", uidParaUpload);
    formDataUpload.append("objetoId", objetoId);
    formDataUpload.append("foto", file);
    formDataUpload.append("tipo", "expediente");

    const resp = await fetch("/uploadFoto", {
      method: "POST",
      body: formDataUpload,
    });

    console.log('[subirArchivoReceta] Respuesta status:', resp.status);

    if (!resp.ok) {
      const errorText = await resp.text();
      console.error('[subirArchivoReceta] Error HTTP:', resp.status, errorText);
      throw new Error(`Error al subir archivo: ${resp.status} - ${errorText}`);
    }

    const data = await resp.json();
    console.log('[subirArchivoReceta] Respuesta JSON:', data);

    if (data.error) {
      console.error('[subirArchivoReceta] Error del servidor:', data.error);
      throw new Error(data.error);
    }

    const path = data.downloadURL || data.url || data.path;
    console.log('[subirArchivoReceta] Path obtenido:', path);
    return path;
  }

  async function handleSaveExtractedData(formData) {
    console.log('[Guardar IA] Iniciando guardado con formData:', {
      tipo: formData.tipo,
      fecha: formData.fecha,
      numMedicamentos: formData.medicamentos?.length || 0,
      medicamentos: formData.medicamentos
    });

    setLoadingAnalysis(true);

    try {
      let recetaUrl = null;
      console.log('[Guardar IA] prescriptionFile:', prescriptionFile ? {
        name: prescriptionFile.name,
        size: prescriptionFile.size,
        type: prescriptionFile.type
      } : 'NULL - No hay archivo');

      if (prescriptionFile) {
        console.log('[Guardar IA] Subiendo archivo de receta...');
        try {
          recetaUrl = await subirArchivoReceta(prescriptionFile);
          console.log('[Guardar IA] Receta subida exitosamente:', recetaUrl);

          if (!recetaUrl) {
            console.error('[Guardar IA] Upload retornó valor vacío');
            toast.error("Error: El servidor no retornó la URL de la receta", { duration: 5000 });
          }
        } catch (uploadError) {
          console.error("[Guardar IA] Error subiendo receta:", uploadError.message);
          console.error("[Guardar IA] Stack:", uploadError.stack);
          toast.error(`No se pudo adjuntar la receta: ${uploadError.message}`, { duration: 6000 });
        }
      } else {
        console.warn('[Guardar IA] No hay prescriptionFile para adjuntar');
      }

      const entryData = {
        fecha: formData.fecha,
        tipo: formData.tipo,
        notas: formData.notas,
        temperatura: formData.temperatura || null,
        peso: formData.peso || null,
        frecuenciaCardiaca: formData.frecuenciaCardiaca || null,
        medicamentos: formData.medicamentos || [],
        recetaUrl: recetaUrl,
        creadoConIA: true,
        titulo: formData.titulo || null,
        resumen: formData.resumen || null,
        clinica: formData.clinica || null,
        veterinario: formData.veterinario || null,
        fechaConsulta: formData.fechaConsulta || null,
        diagnostico: formData.diagnostico || null
      };

      console.log('[Guardar IA] Enviando a API:', {
        objetoId,
        entryData: {
          ...entryData,
          medicamentos: `${entryData.medicamentos.length} medicamentos`
        }
      });

      const resp = await crearEntradaExpediente(objetoId, entryData);

      console.log('[Guardar IA] Respuesta de API:', resp);

      setLoadingAnalysis(false);

      if (!resp.error) {
        console.log('[Guardar IA] Éxito! Nueva entrada:', resp.data);
        onEntryCreated(resp.data);
        setShowReviewModal(false);
        setExtractedData(null);
        setPrescriptionFile(null);
        toast.success("Entrada creada exitosamente con IA");

        const savedEntry = resp.data;
        if (!isVetAccess && savedEntry.medicamentos?.some(m => m.frecuenciaHoras && m.duracionDias)) {
          onPostSaveTreatment(savedEntry);
        }
      } else {
        console.error('[Guardar IA] Error del backend:', resp.error);
        toast.error(resp.error, { duration: 6000 });
      }
    } catch (error) {
      console.error('[Guardar IA] Error:', error);
      setLoadingAnalysis(false);
      toast.error("Error al guardar la entrada: " + error.message);
    }
  }

  function handleCloseAll() {
    setShowEntryMethodModal(false);
    setShowUploadModal(false);
    setShowReviewModal(false);
    setExtractedData(null);
    setAnalysisError(null);
    setLoadingAnalysis(false);
    setPrescriptionFile(null);
    onClose();
  }

  return (
    <>
      <EntryMethodModal
        show={showEntryMethodModal}
        onClose={handleCloseAll}
        onSelectAI={handleSelectAI}
        onSelectManual={handleSelectManual}
      />

      <UploadPrescriptionModal
        show={showUploadModal}
        onClose={handleCloseAll}
        onAnalyze={handleAnalyzeReceta}
        onFillManually={handleSelectManual}
        loading={loadingAnalysis}
        error={analysisError}
      />

      <ReviewExtractedDataModal
        show={showReviewModal}
        onClose={handleCloseAll}
        onSave={handleSaveExtractedData}
        onRescan={handleRescan}
        extractedData={extractedData}
        loading={loadingAnalysis}
      />
    </>
  );
}
